import { useState, useEffect } from 'react';
import { useAuthHeader, useAuth } from '@/hooks/useAuth';
import {
  getInterStoreTransferJobs,
  downloadInterStoreTransferJobFile,
  type InterStoreTransferJob,
} from '@/lib/api/inter-store-transfer-jobs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Loader2,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

// エラーメッセージの切り詰め用ユーティリティ
const truncateErrorMessage = (
  message: string,
  maxLength: number = 500,
): { truncated: string; isTruncated: boolean } => {
  if (!message || message.length <= maxLength) {
    return { truncated: message, isTruncated: false };
  }

  // 最後の改行位置を探して、そこで切り詰める（可読性向上）
  const truncated = message.substring(0, maxLength);
  const lastNewlineIndex = truncated.lastIndexOf('\n');
  const cutPoint = lastNewlineIndex > maxLength * 0.7 ? lastNewlineIndex : maxLength;

  return {
    truncated: message.substring(0, cutPoint) + '...',
    isTruncated: true,
  };
};

// エラーメッセージ表示コンポーネント
interface ErrorMessageDisplayProps {
  errorMessage: string;
}

function ErrorMessageDisplay({ errorMessage }: ErrorMessageDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { truncated, isTruncated } = truncateErrorMessage(errorMessage, 500);

  return (
    <div>
      <h3 className="font-semibold mb-2 text-red-600">エラーメッセージ</h3>
      <div className="bg-red-50 border border-red-200 rounded p-3">
        <div className="text-sm font-mono whitespace-pre-wrap break-words overflow-hidden">
          {isExpanded ? errorMessage : truncated}
        </div>
        {isTruncated && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-6 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-100"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 mr-1" />
                省略表示
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                もっと見る ({errorMessage.length.toLocaleString()}文字)
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

interface InterStoreTransferJobsProps {
  account: {
    id: string;
    code: string;
    name: string;
  };
}

export function InterStoreTransferJobs({ account }: InterStoreTransferJobsProps) {
  const { getAuthHeader, isLoading: authHeaderLoading } = useAuthHeader();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<InterStoreTransferJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [showOnlyMyJobs, setShowOnlyMyJobs] = useState(true); // デフォルト：自分のジョブのみ表示

  // ジョブ一覧を取得
  const fetchJobs = async () => {
    if (authHeaderLoading) return;

    try {
      setLoading(true);
      setError(null);
      const authHeaders = getAuthHeader();

      // 自分のジョブのみ表示する場合はログインユーザーのemailを渡す
      const executedBy = showOnlyMyJobs ? user?.email : undefined;

      const jobList = await getInterStoreTransferJobs(
        account.id,
        authHeaders,
        50,
        executedBy || undefined,
      );
      setJobs(jobList);
    } catch (error) {
      console.error('ジョブ一覧取得エラー:', error);
      setError('ジョブ一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [account.id, authHeaderLoading, showOnlyMyJobs, user?.email]);

  // ファイルダウンロード処理
  const handleFileDownload = async (jobId: string, fileType: string, fileName?: string) => {
    if (!fileName) {
      setError('ファイルが存在しません');
      return;
    }

    const downloadKey = `${jobId}-${fileType}`;
    if (downloadingFiles.has(downloadKey)) return;

    try {
      setDownloadingFiles((prev) => new Set(prev).add(downloadKey));
      setError(null);
      const authHeaders = getAuthHeader();
      await downloadInterStoreTransferJobFile(account.id, jobId, fileType, authHeaders);
    } catch (error) {
      console.error('ファイルダウンロードエラー:', error);
      setError('ファイルのダウンロードに失敗しました');
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  // ジョブステータスのバッジ表示
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            完了
          </Badge>
        );
      case 'running':
        return (
          <Badge variant="default" className="bg-blue-500">
            <Clock className="w-3 h-3 mr-1" />
            実行中
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            失敗
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary">
            <AlertCircle className="w-3 h-3 mr-1" />
            待機中
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          店間移動効果検証 実行ログ
          <Button onClick={fetchJobs} disabled={loading} size="sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            更新
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2 mb-4">
          <Checkbox
            id="show-only-my-jobs"
            checked={showOnlyMyJobs}
            onCheckedChange={(checked) => setShowOnlyMyJobs(checked === true)}
          />
          <label
            htmlFor="show-only-my-jobs"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            自分のジョブのみ表示
          </label>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && jobs.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            読み込み中...
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">実行ログがありません</div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ステータス</TableHead>
                  <TableHead>実行者</TableHead>
                  <TableHead>在庫計上日</TableHead>
                  <TableHead>集計期間</TableHead>
                  <TableHead>実行率期間</TableHead>
                  <TableHead>実行開始</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{getStatusBadge(job.job_status)}</TableCell>
                    <TableCell className="font-medium">{job.executed_by}</TableCell>
                    <TableCell>{job.inventory_date}</TableCell>
                    <TableCell>
                      {job.start_date} 〜 {job.end_date}
                    </TableCell>
                    <TableCell>
                      {job.execution_rate_start_date} 〜 {job.execution_rate_end_date}
                    </TableCell>
                    <TableCell>
                      {job.execution_start_time ? (
                        <span className="text-sm">
                          {format(new Date(job.execution_start_time), 'MM/dd HH:mm', {
                            locale: ja,
                          })}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <JobDetailDialog
                          job={job}
                          onFileDownload={handleFileDownload}
                          downloadingFiles={downloadingFiles}
                        />
                        {job.job_status === 'completed' && job.result_files_name && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleFileDownload(job.id, 'result_files', job.result_files_name)
                            }
                            disabled={downloadingFiles.has(`${job.id}-result_files`)}
                          >
                            {downloadingFiles.has(`${job.id}-result_files`) ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Download className="w-3 h-3 mr-1" />
                            )}
                            結果
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ジョブ詳細ダイアログコンポーネント
interface JobDetailDialogProps {
  job: InterStoreTransferJob;
  onFileDownload: (jobId: string, fileType: string, fileName?: string) => void;
  downloadingFiles: Set<string>;
}

function JobDetailDialog({ job, onFileDownload, downloadingFiles }: JobDetailDialogProps) {
  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return '-';
    try {
      return format(new Date(dateTime), 'yyyy年MM月dd日 HH:mm:ss', { locale: ja });
    } catch {
      return dateTime;
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'yyyy年MM月dd日', { locale: ja });
    } catch {
      return date;
    }
  };

  const FileDownloadButton = ({
    jobId,
    fileType,
    fileName,
    label,
  }: {
    jobId: string;
    fileType: string;
    fileName?: string;
    label: string;
  }) => {
    const downloadKey = `${jobId}-${fileType}`;
    const isDownloading = downloadingFiles.has(downloadKey);

    if (!fileName) {
      return (
        <Button size="sm" variant="outline" disabled>
          {label} (未設定)
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onFileDownload(jobId, fileType, fileName)}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader2 className="w-3 h-3 animate-spin mr-1" />
        ) : (
          <Download className="w-3 h-3 mr-1" />
        )}
        {label}
      </Button>
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Eye className="w-3 h-3 mr-1" />
          詳細
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ジョブ詳細 - {job.id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* 基本情報 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">基本情報</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">実行者:</span> {job.executed_by}
                </div>
                <div>
                  <span className="font-medium">ステータス:</span> {job.job_status}
                </div>
                <div>
                  <span className="font-medium">作成日時:</span> {formatDateTime(job.created_at)}
                </div>
                <div>
                  <span className="font-medium">実行開始:</span>{' '}
                  {formatDateTime(job.execution_start_time)}
                </div>
                <div>
                  <span className="font-medium">実行終了:</span>{' '}
                  {formatDateTime(job.execution_end_time)}
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">分析条件</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">在庫計上日:</span> {formatDate(job.inventory_date)}
                </div>
                <div>
                  <span className="font-medium">集計期間:</span> {formatDate(job.start_date)} 〜{' '}
                  {formatDate(job.end_date)}
                </div>
                <div>
                  <span className="font-medium">実行率期間:</span>{' '}
                  {formatDate(job.execution_rate_start_date)} 〜{' '}
                  {formatDate(job.execution_rate_end_date)}
                </div>
                <div>
                  <span className="font-medium">移動費:</span>{' '}
                  {job.transfer_cost ? `${job.transfer_cost}円` : '未設定'}
                </div>
                <div>
                  <span className="font-medium">出力単位:</span> {job.output_unit}
                </div>
              </div>
            </div>
          </div>

          {/* エラーメッセージ */}
          {job.error_message && <ErrorMessageDisplay errorMessage={job.error_message} />}

          {/* アップロードファイル */}
          <div>
            <h3 className="font-semibold mb-2">アップロードファイル</h3>
            <div className="grid grid-cols-1 gap-2">
              <FileDownloadButton
                jobId={job.id}
                fileType="transfer_order"
                fileName={job.transfer_order_file_name}
                label="移動指示書"
              />
            </div>
          </div>

          {/* 結果ファイル */}
          {job.job_status === 'completed' && job.result_files_name && (
            <div>
              <h3 className="font-semibold mb-2">結果ファイル</h3>
              <FileDownloadButton
                jobId={job.id}
                fileType="result_files"
                fileName={job.result_files_name}
                label="結果ファイル (ZIP)"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
